/**
 * Real OPFS-backed `SenderSource` (docs/file-transfer-resume.md §3.1.4) —
 * the sender's write-through plaintext copy, `ft/send-<transferId>`.
 *
 * API pin (§3.1.4 point 2): the sender path uses the async page-context OPFS
 * API — `FileSystemFileHandle.createWritable()` for the write-through copy,
 * and resume-time `getFile()` + `slice()`/offset reads for pass 2 — because
 * `createSyncAccessHandle` is dedicated-worker-only and the sender loop is
 * panel-hosted (§3.1.2). This is the SENDER seam only; the opfsWriter worker
 * (TASK-17) is the receiver seam and is not used here.
 *
 * One instance is scoped to a single transferId; construct via
 * `createOpfsSenderSource` right before use (fresh send or resume alike).
 */

import { CHUNK_SIZE } from "@reflecto/core";
import { ftLog } from "./ftLog";
import {
  detectOpfsStorage,
  type OpfsStorage,
  type OpfsStorageDeps,
  requireOpfsStorage,
} from "./opfsSeam";
import type { SenderSource } from "./sendFile";
import { FT_OPFS_QUOTA_MARGIN_BYTES } from "./state";

async function getFtDirectory(
  storage: OpfsStorage,
  create: boolean
): Promise<FileSystemDirectoryHandle> {
  const root = await storage.getDirectory();
  return root.getDirectoryHandle("ft", { create });
}

/**
 * Deletes the WHOLE `ft/` directory (both `send-*` and, once TASK-15/17
 * ship, `recv-*`) — the unpair wipe (§3.1.3): "the wipe also deletes the
 * entire OPFS ft/ directory". Best-effort; never throws (no `ft/` directory
 * yet is the common case for a device that never sent/received a file).
 * OPFS is available from any context (page, worker, service worker), so
 * this is safe to call from the background SW's `handlePairingLost`.
 */
export async function deleteAllOpfsTransfers(
  storage: OpfsStorage | null = detectOpfsStorage()
): Promise<void> {
  // NO-OP rather than StorageUnsupportedError when there is no OPFS, and that is a
  // deliberate departure from the "throw on missing storage" rule the factories follow.
  // This is a WIPE: on a platform that cannot hold an `ft/` directory there is nothing
  // to delete, so "unsupported" is not a failure — it is the desired end state. It also
  // runs on the unpair path, where throwing would turn a clean disconnect into an error
  // for a host that never had OPFS. The rule it still obeys is the one that matters:
  // never a TypeError.
  if (!storage) return;
  try {
    const root = await storage.getDirectory();
    await root.removeEntry("ft", { recursive: true });
  } catch (err) {
    // Audit fix (#818 Part 4 sweep): "no ft/ directory" and "the wipe FAILED"
    // are not the same outcome and must not share one silence. `ft/` holds
    // PLAINTEXT copies of every file sent/received, so a failed unpair wipe
    // leaves decrypted user data on disk after the user believes it is gone —
    // that must leave a trace. NotFoundError (never had an ft/ dir) stays
    // silent; anything else logs its error class (never a path or file name).
    if ((err as Error)?.name !== "NotFoundError") {
      ftLog("terminal", {
        role: "sender",
        outcome: "failed",
        reason: `opfs_wipe_failed:${(err as Error)?.name ?? "unknown"}`,
      });
    }
  }
}

/**
 * Deletes ONE transfer artifact from the shared `ft/` directory by file name
 * (`send-<transferId>` / `recv-<transferId>`). Best-effort; never throws.
 * OPFS is available from any context incl. the background SW — this is the
 * artifact-cleanup seam for the SW-side paused-record terminal transitions
 * (`background/pausedTransferTerminal.ts`), which run where no
 * panel-hosted `SenderSource` / offscreen worker-backed output exists.
 */
export async function deleteOpfsTransferFile(
  fileName: string,
  storage: OpfsStorage | null = detectOpfsStorage()
): Promise<void> {
  // No-op without OPFS, same reasoning as `deleteAllOpfsTransfers`: this is
  // best-effort artifact cleanup, and a platform that cannot hold the artifact has
  // nothing to clean. Never a TypeError.
  if (!storage) return;
  try {
    const dir = await getFtDirectory(storage, false);
    await dir.removeEntry(fileName);
  } catch {
    // No ft/ directory or no such file — already clean.
  }
}

/** Build the real `SenderSource` for `transferId`. Never throws at construction — all I/O is deferred to the returned methods. */
export function createOpfsSenderSource(transferId: string, deps: OpfsStorageDeps): SenderSource {
  // Resolved at the point of use rather than here — see the note in
  // `receiverWorkerClient`. Every method below that touches OPFS names itself, so an
  // unsupported platform gets a message pointing at the operation it attempted.
  const storageFor = (operation: string) => requireOpfsStorage(deps.storage, operation);
  const fileName = `send-${transferId}`;
  // The writable stream is opened lazily on the first `appendChunk` call and
  // stays open across calls (re-opening per chunk would truncate the file
  // each time). `readChunks`/`deleteCopy` close it first — OPFS write data
  // isn't visible/durable until `close()`.
  let writable: FileSystemWritableFileStream | null = null;

  async function closeWritable(): Promise<void> {
    if (!writable) return;
    const w = writable;
    writable = null;
    await w.close();
  }

  return {
    async checkQuota(fileSize: number): Promise<boolean> {
      const estimate = await storageFor("checkQuota()").estimate();
      const quota = estimate.quota ?? 0;
      const usage = estimate.usage ?? 0;
      return fileSize <= quota - usage - FT_OPFS_QUOTA_MARGIN_BYTES;
    },

    async appendChunk(chunk: Uint8Array): Promise<void> {
      if (!writable) {
        const dir = await getFtDirectory(storageFor("appendChunk()"), true);
        const handle = await dir.getFileHandle(fileName, { create: true });
        writable = await handle.createWritable(); // truncates to empty — correct: pass 1 always starts a fresh copy
      }
      await writable.write(chunk as BufferSource);
    },

    async *readChunks(
      startIndex: number,
      endIndexInclusive: number
    ): AsyncGenerator<{ index: number; data: Uint8Array }> {
      await closeWritable();
      const dir = await getFtDirectory(storageFor("readChunks()"), true);
      const handle = await dir.getFileHandle(fileName, { create: false });
      const file = await handle.getFile();
      for (let i = startIndex; i <= endIndexInclusive; i++) {
        const offset = i * CHUNK_SIZE;
        const end = Math.min(offset + CHUNK_SIZE, file.size);
        const buf = new Uint8Array(await file.slice(offset, end).arrayBuffer());
        yield { index: i, data: buf };
      }
    },

    async deleteCopy(): Promise<void> {
      await closeWritable().catch(() => {});
      const dir = await getFtDirectory(storageFor("deleteCopy()"), false).catch(() => null);
      if (!dir) return;
      await dir.removeEntry(fileName).catch(() => {});
    },

    async getSize(): Promise<number | null> {
      await closeWritable();
      const dir = await getFtDirectory(storageFor("getSize()"), false).catch(() => null);
      if (!dir) return null;
      const handle = await dir.getFileHandle(fileName, { create: false }).catch(() => null);
      if (!handle) return null;
      const file = await handle.getFile();
      return file.size;
    },
  };
}
